import { parseCsvFile, type CsvParseOptions } from '@repo/commons/utils'

export interface ValidationError {
	group: string
	errors: string[]
}

export interface ValidationWarning {
	group: string
	warnings: string[]
}

export interface ValidationResult {
	isValid: boolean
	errors: ValidationError[]
	warnings: ValidationWarning[]
}

export interface FileValidationResult<T = Record<string, unknown>> {
	file: File
	data?: T[]
	errors: ValidationError[]
	warnings: ValidationWarning[]
}

export type FieldValidator<T, K extends keyof T> = (value: T[K], rowIndex: number) => string | null

export interface CsvSchema<T> {
	required: (keyof T)[]
	optional?: (keyof T)[]
	validators?: Partial<Record<keyof T, FieldValidator<T, keyof T>>>
}

export const createFieldValidators = () => ({
	email: (value: unknown, rowIndex: number): string | null => {
		if (!value) return null
		if (typeof value !== 'string') {
			return `Row ${rowIndex + 1}: Email must be a string`
		}
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		if (!emailRegex.test(value)) {
			return `Row ${rowIndex + 1}: Invalid email format`
		}
		return null
	},

	url: (value: unknown, rowIndex: number): string | null => {
		if (!value) return null
		if (typeof value !== 'string') {
			return `Row ${rowIndex + 1}: URL must be a string`
		}
		try {
			new URL(value)
			return null
		} catch {
			return `Row ${rowIndex + 1}: Invalid URL format`
		}
	},

	minLength:
		(min: number) =>
		(value: unknown, rowIndex: number): string | null => {
			if (!value) return null
			if (typeof value !== 'string') {
				return `Row ${rowIndex + 1}: Value must be a string`
			}
			if (value.length < min) {
				return `Row ${rowIndex + 1}: Must be at least ${min} characters long`
			}
			return null
		},

	maxLength:
		(max: number) =>
		(value: unknown, rowIndex: number): string | null => {
			if (!value) return null
			if (typeof value !== 'string') {
				return `Row ${rowIndex + 1}: Value must be a string`
			}
			if (value.length > max) {
				return `Row ${rowIndex + 1}: Must be no more than ${max} characters long`
			}
			return null
		},

	numeric: (value: unknown, rowIndex: number): string | null => {
		if (!value) return null
		if (typeof value === 'number') return null
		if (typeof value === 'string' && !isNaN(Number(value))) return null
		return `Row ${rowIndex + 1}: Must be a valid number`
	},

	oneOf:
		<TValue extends string>(allowedValues: TValue[]) =>
		(value: unknown, rowIndex: number): string | null => {
			if (!value) return null
			if (typeof value !== 'string') {
				return `Row ${rowIndex + 1}: Value must be a string`
			}
			if (!allowedValues.includes(value as TValue)) {
				return `Row ${rowIndex + 1}: Must be one of: ${allowedValues.join(', ')}`
			}
			return null
		},

	boolean: (value: unknown, rowIndex: number): string | null => {
		if (!value) return null
		const stringValue = String(value).toLowerCase()
		const validBooleans = ['true', 'false', '1', '0', 'yes', 'no']
		if (!validBooleans.includes(stringValue)) {
			return `Row ${rowIndex + 1}: Must be a valid boolean (true/false, 1/0, yes/no)`
		}
		return null
	},

	date: (value: unknown, rowIndex: number): string | null => {
		if (!value) return null
		const date = new Date(String(value))
		if (isNaN(date.getTime())) {
			return `Row ${rowIndex + 1}: Must be a valid date`
		}
		return null
	}
})

export const createHeaderValidator = <T extends Record<string, unknown>>(schema: CsvSchema<T>) => {
	return (data: T[]): ValidationError[] => {
		if (data.length === 0) {
			return [{ group: 'Data Error', errors: ['No data found'] }]
		}

		const firstRow = data[0]
		const actualHeaders = Object.keys(firstRow) as (keyof T)[]
		const allExpectedHeaders = [...schema.required, ...(schema?.optional ?? [])]

		const errors: string[] = []

		// Check for missing required headers
		const missingRequired = schema.required.filter((header) => !actualHeaders.includes(header))

		if (missingRequired.length > 0) {
			errors.push(`Missing required headers: ${missingRequired.map(String).join(', ')}`)
		}

		// Check for unexpected headers
		if (schema?.optional?.length) {
			const unexpectedHeaders = actualHeaders.filter(
				(header) => !allExpectedHeaders.includes(header)
			)

			if (unexpectedHeaders.length > 0) {
				errors.push(`Unexpected headers found: ${unexpectedHeaders.map(String).join(', ')}`)
			}
		}

		return errors.length > 0 ? [{ group: 'Invalid Headers', errors }] : []
	}
}

export const createDataValidator = <T extends Record<string, unknown>>(schema: CsvSchema<T>) => {
	return (data: T[]): ValidationError[] => {
		const errors: string[] = []

		data.forEach((row, index) => {
			// Validate required fields
			schema.required.forEach((field) => {
				const value = row[field]
				const isBlank =
					value === undefined ||
					value === null ||
					(typeof value === 'string' && value.trim() === '')
				if (isBlank) {
					errors.push(`Row ${index + 1}: "${String(field)}" cannot be empty`)
				}
			})

			// Run custom validators if provided
			if (schema.validators) {
				;(Object.entries(schema.validators) as [keyof T, FieldValidator<T, keyof T>][]).forEach(
					([field, validator]) => {
						if (validator) {
							const error = validator(row[field], index)
							if (error) {
								errors.push(error)
							}
						}
					}
				)
			}
		})

		return errors.length > 0 ? [{ group: 'Missing Required Values', errors }] : []
	}
}

export const createCsvValidator = <T extends Record<string, unknown>>(
	schema: CsvSchema<T>,
	options: CsvParseOptions = {}
) => {
	const validateHeaders = createHeaderValidator(schema)
	const validateData = createDataValidator(schema)

	const validateFile = async (file: File): Promise<FileValidationResult<T>> => {
		const result: FileValidationResult<T> = {
			file,
			errors: [],
			warnings: []
		}

		try {
			const parseResult = await parseCsvFile<T>(file, options)

			if (parseResult.errors.length > 0) {
				result.errors = [
					{
						group: file.name,
						errors: parseResult.errors.map((error) => error.message)
					}
				]
				return result
			}

			result.data = parseResult.data

			// Validate headers
			const headerErrors = validateHeaders(parseResult.data)
			if (headerErrors.length > 0) {
				result.errors.push(...headerErrors)
				return result
			}

			// Validate data
			const dataErrors = validateData(parseResult.data)
			result.errors.push(...dataErrors)
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : String(error)
			result.errors = [
				{
					group: 'Parsing Error',
					errors: [`Failed to parse CSV - ${errorMessage}`]
				}
			]
		}

		return result
	}

	const validateFiles = async (files: File[]): Promise<FileValidationResult<T>[]> => {
		return Promise.all(files.map(validateFile))
	}

	return {
		validateFile,
		validateFiles,
		validateHeaders,
		validateData
	}
}
